import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Users, TrendingUp, Loader2, Eye, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useYear } from '../contexts/YearContext';
import { useAuth } from '../contexts/AuthContext';
import { getRoleDisplayName, getRoleColor } from '../utils/roleMapping';

function TeamDashboard() {
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMember, setSelectedMember] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table', 'cards'
  const [sortByIncome, setSortByIncome] = useState('desc'); // 'desc', 'asc', 'hierarchy'
  const { selectedYear } = useYear();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadTeamData();
  }, [selectedYear]);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/team/aggregate?year=${selectedYear}`);
      setTeamData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading team data:', err);
      if (err.response?.status === 403) {
        setError('Team mode not enabled or you do not have permission to view team data');
      } else {
        setError('Failed to load team data');
      }
    } finally {
      setLoading(false);
    }
  };

  const viewMemberDashboard = (memberId) => {
    navigate(`/team/member/${memberId}`);
  };

  const handleMemberSelect = (e) => {
    const memberId = e.target.value;
    setSelectedMember(memberId);
    if (memberId) {
      navigate(`/team/member/${memberId}`);
    }
  };

  // Sort members hierarchically or by income
  const getSortedMembers = () => {
    if (!teamData?.members) return [];
    
    const members = [...teamData.members];
    
    if (sortByIncome === 'hierarchy') {
      // Multi-level hierarchical sort: Satellite → FSL → FTA → Agents
      // Each leader's downline appears directly below them
      const satellites = members.filter(m => m.role?.toLowerCase() === 'satellite');
      const fsls = members.filter(m => m.role?.toLowerCase() === 'fsl');
      const ftas = members.filter(m => m.role?.toLowerCase() === 'fta');
      const agents = members.filter(m => m.role?.toLowerCase() === 'agent');
      
      const sorted = [];
      const addedIds = new Set();
      
      // Helper function to recursively add team members under a leader
      const addDownline = (leaderId) => {
        // First, find and add FSLs reporting to this leader
        const leaderFSLs = fsls.filter(fsl => fsl.leader_id === leaderId && !addedIds.has(fsl.id));
        leaderFSLs.forEach(fsl => {
          sorted.push(fsl);
          addedIds.add(fsl.id);
          // Add FSL's downline (FTAs and their agents)
          addDownline(fsl.id);
        });
        
        // Then, find and add FTAs reporting to this leader
        const leaderFTAs = ftas.filter(fta => fta.leader_id === leaderId && !addedIds.has(fta.id));
        leaderFTAs.forEach(fta => {
          sorted.push(fta);
          addedIds.add(fta.id);
          // Add FTA's agents
          const ftaAgents = agents.filter(agent => agent.leader_id === fta.id && !addedIds.has(agent.id));
          ftaAgents.forEach(agent => {
            sorted.push(agent);
            addedIds.add(agent.id);
          });
        });
        
        // Finally, add any agents directly reporting to this leader (no FTA in between)
        const directAgents = agents.filter(agent => agent.leader_id === leaderId && !addedIds.has(agent.id));
        directAgents.forEach(agent => {
          sorted.push(agent);
          addedIds.add(agent.id);
        });
      };
      
      // Start with satellites (top level)
      satellites.forEach(satellite => {
        sorted.push(satellite);
        addedIds.add(satellite.id);
        addDownline(satellite.id);
      });
      
      // If viewing as FSL (no satellite in data), start with FSLs
      if (satellites.length === 0 && fsls.length > 0) {
        fsls.filter(fsl => !addedIds.has(fsl.id)).forEach(fsl => {
          sorted.push(fsl);
          addedIds.add(fsl.id);
          addDownline(fsl.id);
        });
      }
      
      // If viewing as FTA only (no satellite or FSL), start with FTAs
      if (satellites.length === 0 && fsls.length === 0 && ftas.length > 0) {
        ftas.filter(fta => !addedIds.has(fta.id)).forEach(fta => {
          sorted.push(fta);
          addedIds.add(fta.id);
          const ftaAgents = agents.filter(agent => agent.leader_id === fta.id && !addedIds.has(agent.id));
          ftaAgents.forEach(agent => {
            sorted.push(agent);
            addedIds.add(agent.id);
          });
        });
      }
      
      // Add any remaining members not yet added (orphans or unlinked)
      const remaining = members.filter(m => !addedIds.has(m.id));
      sorted.push(...remaining);
      
      return sorted;
    } else {
      // Sort by income
      return members.sort((a, b) => {
        const incomeA = a.ytd_income || 0;
        const incomeB = b.ytd_income || 0;
        return sortByIncome === 'desc' ? incomeB - incomeA : incomeA - incomeB;
      });
    }
  };

  const toggleIncomeSort = () => {
    if (sortByIncome === 'hierarchy') {
      setSortByIncome('desc');
    } else if (sortByIncome === 'desc') {
      setSortByIncome('asc');
    } else {
      setSortByIncome('hierarchy');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-4">
            {user?.team_logo && (
              <div className="flex-shrink-0">
                <img 
                  src={user.team_logo} 
                  alt="Team Logo" 
                  className="w-16 h-16 object-contain rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 p-1"
                />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {user?.team_name || 'My Team'}
              </h1>
              <p className="text-gray-600 dark:text-gray-300">View and manage your team members for {selectedYear}</p>
            </div>
          </div>
          
          {/* Team Member Dropdown Selector */}
          {teamData?.members?.length > 0 && (
            <div className="sm:min-w-[280px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Quick Access
              </label>
              <div className="relative">
                <select
                  value={selectedMember}
                  onChange={handleMemberSelect}
                  className="w-full appearance-none bg-white dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 pr-10 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors cursor-pointer"
                >
                  <option value="">Select a team member...</option>
                  {teamData.members.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name} ({getRoleDisplayName(member.role)})
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Aggregate Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Total Team Income */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold">Total Team Income</h2>
            <TrendingUp className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-4xl font-bold">${teamData?.total_team_income?.toLocaleString() || '0.00'}</p>
          <p className="text-sm opacity-90 mt-2">Year-to-date for {selectedYear}</p>
        </div>

        {/* Total Team Members */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold">Team Members</h2>
            <Users className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-4xl font-bold">{teamData?.total_team_members || 0}</p>
          <p className="text-sm opacity-90 mt-2">Active members in your downline</p>
        </div>
      </div>

      {/* Team Members Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        {/* Header with View Toggle and Sort */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Team Members</h2>
          
          <div className="flex items-center space-x-3">
            {/* Sort Toggle */}
            <button
              onClick={toggleIncomeSort}
              className="flex items-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors text-sm font-medium text-gray-700 dark:text-gray-300"
              title="Toggle sort order"
            >
              <span>Sort:</span>
              {sortByIncome === 'hierarchy' && (
                <>
                  <span>By Team</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </>
              )}
              {sortByIncome === 'desc' && (
                <>
                  <span>Income ↓</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
              {sortByIncome === 'asc' && (
                <>
                  <span>Income ↑</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </>
              )}
            </button>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  viewMode === 'table' 
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
                title="Table View"
              >
                Table
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  viewMode === 'cards' 
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
                title="Card View"
              >
                Cards
              </button>
            </div>
          </div>
        </div>
        
        {teamData?.members?.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Team Members Yet</h3>
            <p className="text-gray-600 dark:text-gray-300">Your team members will appear here once they register</p>
          </div>
        ) : (
          <>
            {/* Table View */}
            {viewMode === 'table' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        YTD Income ({selectedYear})
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:border-gray-700">
                    {getSortedMembers().map((member, index, array) => {
                      const role = member.role?.toLowerCase();
                      const isSatellite = role === 'satellite';
                      const isFSL = role === 'fsl';
                      const isFTA = role === 'fta';
                      const isAgent = role === 'agent';
                      const prevMember = index > 0 ? array[index - 1] : null;
                      
                      // Determine indentation level for hierarchy view
                      let indent = '';
                      let prefix = '';
                      if (sortByIncome === 'hierarchy') {
                        if (isFSL) {
                          indent = 'pl-4';
                          prefix = '↳ ';
                        } else if (isFTA) {
                          indent = 'pl-8';
                          prefix = '├─ ';
                        } else if (isAgent) {
                          indent = 'pl-12';
                          prefix = '└─ ';
                        }
                      }
                      
                      return (
                        <tr 
                          key={member.id}
                          className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            (isSatellite || isFSL) && sortByIncome === 'hierarchy' ? 'bg-purple-50 dark:bg-purple-900/10 font-semibold' : ''
                          } ${
                            isFTA && sortByIncome === 'hierarchy' ? 'bg-blue-50 dark:bg-blue-900/10' : ''
                          }`}
                        >
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className={`font-medium text-gray-900 dark:text-white ${indent}`}>
                              {sortByIncome === 'hierarchy' && prefix && (
                                <span className="mr-2 text-gray-400">{prefix}</span>
                              )}
                              {member.name}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(member.role)}`}>
                              {getRoleDisplayName(member.role)}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                            {member.email}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right font-semibold text-green-600 dark:text-green-400">
                            ${member.ytd_income?.toLocaleString() || '0.00'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-center">
                            <button
                              onClick={() => viewMemberDashboard(member.id)}
                              className="inline-flex items-center px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Card View */}
            {viewMode === 'cards' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getSortedMembers().map((member) => {
                  const role = member.role?.toLowerCase();
                  const isSatellite = role === 'satellite';
                  const isFSL = role === 'fsl';
                  const isFTA = role === 'fta';
                  const isAgent = role === 'agent';
                  
                  let cardStyle = '';
                  let prefix = '';
                  let indent = '';
                  
                  if (sortByIncome === 'hierarchy') {
                    if (isSatellite) {
                      cardStyle = 'ring-2 ring-purple-400 dark:ring-purple-600 bg-purple-50 dark:bg-purple-900/10';
                    } else if (isFSL) {
                      cardStyle = 'ring-2 ring-purple-300 dark:ring-purple-700 bg-purple-50 dark:bg-purple-900/10';
                      prefix = '↳ ';
                      indent = 'ml-0 md:ml-4';
                    } else if (isFTA) {
                      cardStyle = 'ring-2 ring-blue-200 dark:ring-blue-700 bg-blue-50 dark:bg-blue-900/10';
                      prefix = '├─ ';
                      indent = 'ml-0 md:ml-8';
                    } else if (isAgent) {
                      prefix = '└─ ';
                      indent = 'ml-0 md:ml-12';
                    }
                  }
                  
                  return (
                    <div 
                      key={member.id}
                      className={`border border-gray-200 dark:border-gray-700 rounded-lg p-5 hover:shadow-lg transition-all cursor-pointer hover:border-blue-300 dark:hover:border-blue-600 ${cardStyle} ${indent}`}
                      onClick={() => viewMemberDashboard(member.id)}
                    >
                      {/* Fixed Height Container */}
                      <div className="flex flex-col h-full">
                        {/* Name - Fixed Height */}
                        <div className="mb-3 min-h-[3rem] flex items-center">
                          {sortByIncome === 'hierarchy' && prefix && (
                            <span className="mr-2 text-gray-400 text-sm">{prefix}</span>
                          )}
                          <h3 className="font-semibold text-gray-900 dark:text-white text-lg line-clamp-2">
                            {member.name}
                          </h3>
                        </div>

                        {/* Role Badge */}
                        <div className="mb-3">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(member.role)}`}>
                            {getRoleDisplayName(member.role)}
                          </span>
                        </div>

                        {/* Email - Fixed Height with Truncate */}
                        <div className="mb-4 min-h-[1.5rem]">
                          <p className="text-sm text-gray-600 dark:text-gray-400 truncate" title={member.email}>
                            {member.email}
                          </p>
                        </div>

                        {/* YTD Income - Pushed to Bottom */}
                        <div className="mt-auto pt-4 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">YTD Income ({selectedYear})</p>
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                            ${member.ytd_income?.toLocaleString() || '0.00'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default TeamDashboard;
